/**
 * Generates vitest type equality tests for zinfer.
 *
 * This module generates test files that verify zinfer-generated types
 * match z.input<typeof Schema> and z.output<typeof Schema>.
 */

import { basename, extname } from "pathe";
import type { MappedTypeName } from "./types.js";

/**
 * Information about a schema for test generation.
 */
export interface TestSchemaInfo {
  /** Schema variable name (e.g., "UserSchema") */
  schemaName: string;
  /** Generated input type name (e.g., "UserInput") */
  inputTypeName: string;
  /** Generated output type name (e.g., "UserOutput") */
  outputTypeName: string;
  /**
   * Whether this schema's *output* carries a `.brand()` marker. Brands never
   * touch the input side, so this only changes how the output assertion is
   * generated.
   */
  hasBrand?: boolean;
}

/**
 * Information about a file for test generation.
 */
export interface TestFileInfo {
  /** Path to the schema source file (for import) */
  schemaFilePath: string;
  /** Path to the generated types file (for import) */
  typesFilePath: string;
  /** Unique prefix for this file's imports (e.g., "Basic" for basic-schema.ts) */
  importPrefix: string;
  /** Schemas in this file */
  schemas: TestSchemaInfo[];
}

/**
 * Options for test generation.
 */
export interface TestGeneratorOptions {
  /** Whether to include a file header comment */
  includeHeader?: boolean;
  /**
   * The `brandStrategy` the types were generated with. Only affects a
   * schema whose `hasBrand` is set: under `"local-symbol"`, its output
   * assertion is generated against zod's own brand marker via a
   * canonicalizing comparison instead of plain `toEqualTypeOf`, since a
   * local-symbol marker is intentionally a different shape from zod's own.
   */
  brandStrategy?: "zod-import" | "local-symbol";
}

/**
 * Converts a kebab-case or snake_case string to PascalCase.
 */
export function toPascalCase(str: string): string {
  return str
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

/**
 * Removes file extension from a path.
 * Handles .ts, .js, .tsx, .jsx, .mts, .mjs, .cts, .cjs extensions.
 */
function removeExtension(filePath: string): string {
  const ext = extname(filePath);
  const supportedExtensions = [".ts", ".js", ".tsx", ".jsx", ".mts", ".mjs", ".cts", ".cjs"];
  if (supportedExtensions.includes(ext)) {
    return filePath.slice(0, -ext.length);
  }
  return filePath;
}

/**
 * Generates a unique prefix from a file path.
 */
export function generateImportPrefix(filePath: string): string {
  const name = removeExtension(basename(filePath));
  return toPascalCase(name);
}

/**
 * Creates TestSchemaInfo from schema name and mapped type names.
 *
 * @param hasBrand - Whether this schema's output carries a `.brand()`
 *   marker. Required for a correct output assertion under
 *   `brandStrategy: "local-symbol"` (see `TestSchemaInfo.hasBrand`) - pass
 *   `containsBrandMarker(result.output)` from the corresponding
 *   `ExtractResult`.
 */
export function createTestSchemaInfo(
  schemaName: string,
  mappedNames: MappedTypeName,
  hasBrand?: boolean,
): TestSchemaInfo {
  return {
    schemaName,
    inputTypeName: mappedNames.inputName,
    outputTypeName: mappedNames.outputName,
    hasBrand,
  };
}

/**
 * Generates vitest type equality test file content.
 */
export class TestGenerator {
  private options: Required<TestGeneratorOptions>;

  constructor(options: TestGeneratorOptions = {}) {
    this.options = {
      includeHeader: options.includeHeader ?? true,
      brandStrategy: options.brandStrategy ?? "zod-import",
    };
  }

  /**
   * Generates the complete test file content.
   *
   * @param files - Array of file information for test generation
   * @returns Generated test file content
   */
  generate(files: TestFileInfo[]): string {
    if (files.length === 0) {
      return "";
    }

    const usesLocalSymbolBrand =
      this.options.brandStrategy === "local-symbol" &&
      files.some((f) => f.schemas.some((s) => s.hasBrand));

    const parts: string[] = [];

    // Header comment
    if (this.options.includeHeader) {
      parts.push(this.generateHeader());
      parts.push("");
    }

    // Core imports
    parts.push(this.generateCoreImports(usesLocalSymbolBrand));
    parts.push("");

    // File-specific imports
    for (const file of files) {
      parts.push(this.generateFileImports(file, usesLocalSymbolBrand));
      parts.push("");
    }

    // Test describe blocks
    parts.push(this.generateTestSuite(files));

    return parts.join("\n");
  }

  /**
   * Generates the header comment.
   */
  private generateHeader(): string {
    return `// This file is auto-generated by zinfer --generate-tests
// Do not edit manually. Regenerate with: zinfer --generate-tests [schema-files] --types-dir [types-dir] --out [output]`;
  }

  /**
   * Generates core import statements.
   *
   * @param usesLocalSymbolBrand - Whether any schema being tested needs the
   *   brand-canonicalizing comparison, which additionally requires the
   *   `__ZinferCanonBrand` utility type this defines.
   */
  private generateCoreImports(usesLocalSymbolBrand: boolean): string {
    if (!usesLocalSymbolBrand) {
      return `import { describe, it, expectTypeOf } from "vitest";
import type { z } from "zod";`;
    }

    return `import { describe, it, expectTypeOf } from "vitest";
import * as __zinferZod from "zod";
import type { z } from "zod";

// A local-symbol brand marker is intentionally a different shape from
// zod's own BRAND, so comparing it to z.output<> directly can never hold.
// This canonicalizes both sides' brand-marker property (whichever unique
// symbol keys it, and whether the tag is a bare literal or zod's own
// { [Tag]: true } encoding) down to a common, always-non-readonly shape and
// tag before comparing - zinfer's own marker is readonly, zod's is not, and
// that alone must not fail the comparison - recursively, so a brand nested
// at any depth, including inside a self-referential schema, is still
// verified. The zod-side brand symbol is picked up by name rather than
// assumed, since it is exported as \`$brand\` on some zod versions and as
// \`BRAND\` (both a value and a type) on others, across the zod
// peerDependencies range this package supports.
type __ZinferSafeGet<T, K extends PropertyKey> = K extends keyof T ? T[K] : never;
type __ZinferZodBrandKey = __ZinferSafeGet<typeof __zinferZod, "$brand"> extends never
  ? __ZinferSafeGet<typeof __zinferZod, "BRAND">
  : __ZinferSafeGet<typeof __zinferZod, "$brand">;
type __ZinferBrandTag<V> = V extends string | number | boolean | symbol | bigint ? V : keyof V;
// A fixed-length tuple can't go through the same [infer U] collapse a plain
// array does - that would widen it to a same-element-type array and lose
// which element is at which position, hiding a genuine positional mismatch.
// Rebuilt index by index instead (T[N] works even once T is intersected
// with the brand's marker property, unlike destructuring it via
// [infer H, ...infer R], which does not), then the marker is intersected
// back in separately, the same way a plain object's is.
type __ZinferBuildTuple<
  T extends readonly unknown[],
  S extends symbol,
  N extends number,
  Acc extends readonly unknown[] = [],
> = Acc["length"] extends N
  ? Acc
  : __ZinferBuildTuple<T, S, N, [...Acc, __ZinferCanonBrand<T[Acc["length"]], S>]>;
type __ZinferCanonBrand<T, S extends symbol> = T extends
  | Date
  | RegExp
  | Error
  | Map<any, any>
  | Set<any>
  | WeakMap<any, any>
  | WeakSet<any>
  | Promise<any>
  | Function
  ? T
  : T extends readonly any[]
    ? (number extends T["length"]
        ? T extends readonly (infer U)[]
          ? __ZinferCanonBrand<U, S>[]
          : never
        : T extends readonly unknown[]
          ? __ZinferBuildTuple<T, S, T["length"]> &
              (S extends keyof T ? { __zinferBrandTag: __ZinferBrandTag<T[S]> } : {})
          : never)
    : T extends object
      ? { [K in keyof T as K extends S ? never : K]: __ZinferCanonBrand<T[K], S> } & (S extends
          keyof T
          ? { __zinferBrandTag: __ZinferBrandTag<T[S]> }
          : {})
      : T;`;
  }

  /**
   * Generates import statements for a single file.
   *
   * @param usesLocalSymbolBrand - Whether the local `__brand` symbol needs
   *   importing for this file (only when it has a branded schema under
   *   `brandStrategy: "local-symbol"`).
   */
  private generateFileImports(file: TestFileInfo, usesLocalSymbolBrand: boolean): string {
    const lines: string[] = [];
    const fileLabel = removeExtension(basename(file.schemaFilePath));
    const fileNeedsLocalBrand = usesLocalSymbolBrand && file.schemas.some((s) => s.hasBrand);

    lines.push(`// --- ${fileLabel} ---`);

    // Remove extension for imports (TypeScript/bundlers handle resolution)
    const schemaPath = removeExtension(file.schemaFilePath);
    const typesPath = removeExtension(file.typesFilePath);

    // Schema imports (runtime values with aliases)
    const schemaImports = file.schemas
      .map((s) => `${s.schemaName} as ${file.importPrefix}${s.schemaName}`)
      .join(", ");
    lines.push(`import { ${schemaImports} } from "${schemaPath}";`);

    // Type imports (with aliases)
    const typeImports = file.schemas.flatMap((s) => [
      `${s.inputTypeName} as ${file.importPrefix}${s.inputTypeName}`,
      `${s.outputTypeName} as ${file.importPrefix}${s.outputTypeName}`,
    ]);
    if (fileNeedsLocalBrand) {
      typeImports.push(`__brand as ${file.importPrefix}__brand`);
    }

    if (typeImports.length <= 2) {
      lines.push(`import type { ${typeImports.join(", ")} } from "${typesPath}";`);
    } else {
      lines.push(`import type {`);
      lines.push(`  ${typeImports.join(",\n  ")},`);
      lines.push(`} from "${typesPath}";`);
    }

    return lines.join("\n");
  }

  /**
   * Generates the test suite with all describe blocks.
   */
  private generateTestSuite(files: TestFileInfo[]): string {
    const describes = files.map((file) => this.generateFileDescribe(file)).join("\n\n");

    return `describe("Type equality tests", () => {
${describes}
});
`;
  }

  /**
   * Generates a describe block for a single file.
   */
  private generateFileDescribe(file: TestFileInfo): string {
    const fileLabel = removeExtension(basename(file.schemaFilePath));
    const tests = file.schemas
      .map((s) => this.generateSchemaTests(file.importPrefix, s))
      .join("\n\n");

    return `  describe("${fileLabel}", () => {
${tests}
  });`;
  }

  /**
   * Generates test cases for a single schema.
   */
  private generateSchemaTests(prefix: string, schema: TestSchemaInfo): string {
    const prefixedSchema = `${prefix}${schema.schemaName}`;
    const prefixedInput = `${prefix}${schema.inputTypeName}`;
    const prefixedOutput = `${prefix}${schema.outputTypeName}`;

    const outputAssertion =
      this.options.brandStrategy === "local-symbol" && schema.hasBrand
        ? `expectTypeOf<__ZinferCanonBrand<${prefixedOutput}, typeof ${prefix}__brand>>().toEqualTypeOf<__ZinferCanonBrand<z.output<typeof ${prefixedSchema}>, __ZinferZodBrandKey>>();`
        : `expectTypeOf<${prefixedOutput}>().toEqualTypeOf<z.output<typeof ${prefixedSchema}>>();`;

    return `    it("${schema.schemaName} input matches z.input", () => {
      expectTypeOf<${prefixedInput}>().toEqualTypeOf<z.input<typeof ${prefixedSchema}>>();
    });

    it("${schema.schemaName} output matches z.output", () => {
      ${outputAssertion}
    });`;
  }
}

/**
 * Convenience function to generate test file content.
 *
 * @param files - Array of file information for test generation
 * @param options - Generation options
 * @returns Generated test file content
 */
export function generateTypeTests(files: TestFileInfo[], options?: TestGeneratorOptions): string {
  const generator = new TestGenerator(options);
  return generator.generate(files);
}
